import React, { useState, useEffect } from 'react';
import { useApp } from '../../../../AppContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, faPlus, faTrash, faChevronUp, faChevronDown, faCog, faColumns, 
  faLink, faDatabase, faGamepad, faCheck, faTimes
} from '@fortawesome/free-solid-svg-icons';
import SearchSelecter from '../../../SearchSelecter';
import ColumnManagement from './ColumnManagement';
import ControlManagement from './ControlManagement';
import RelationTableSettings from './RelationTableSettings';
import styled from 'styled-components';

// 모바일 최적화된 스타일 컴포넌트들
const MobileContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  padding: 16px;
  background: #f8f9fa;
  overflow-y: auto;
  z-index: 1000;
`;

const PageHeader = styled.div`
  background: #ffffff;
  color: #2c3e50;
  padding: 24px 16px;
  border-radius: 12px;
  margin-bottom: 16px;
  position: relative;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const PageSubtitle = styled.p`
  margin: 8px 0 0 0;
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  color: #6c757d;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    color: #495057;
    transform: scale(1.05);
  }
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  background: ${props => props.isExpanded ? props.color || '#007bff' : '#6c757d'};
  color: white;
  padding: 16px 20px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
`;

const SectionIcon = styled.div`
  font-size: 18px;
`;

const SectionContent = styled.div`
  padding: 20px;
  display: ${props => props.isExpanded ? 'block' : 'none'};
`;

const FormCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const FormTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #495057;
`;

const MobileInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  
  &:disabled {
    background: #e9ecef;
    color: #6c757d;
  }
`;

const MobileCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin: 0;
  }
  
  span {
    font-size: 14px;
    color: #495057;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid ${props => props.selected ? '#007bff' : '#e9ecef'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #007bff;
    background: #f8f9fa;
  }
  
  input[type="radio"] {
    width: 16px;
    height: 16px;
    margin: 0;
  }
  
  span {
    font-size: 14px;
    color: #495057;
    font-weight: ${props => props.selected ? '600' : '400'};
  }
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: ${props => props.variant === 'primary' ? '#007bff' : '#28a745'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#0056b3' : '#1e7e34'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TableCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  margin: 16px 0;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`;

const TableRow = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ColumnItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f3f4;
`;

const ColumnName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`;

const ColumnFields = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px 16px;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectHeader = styled.div`
  border: 1px solid #ced4da;
  padding: 12px 16px;
  border-radius: 6px;
  cursor: pointer;
  background: white;
  color: #495057;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  
  &:hover {
    border-color: #007bff;
  }
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileManageBoard = ({ tableName, setValues, values, menuConfig, setMenuConfig, onClose }) => {
  const { user } = useApp();
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [expandedSections, setExpandedSections] = useState({
    control: true,
    column: false,
    relationTable: false,
    columnManage: false,
    controlManage: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const showMessage = (message, isError = false) => {
    if (isError) {
      setErrorMessage(message);
      setSuccessMessage('');
    } else {
      setSuccessMessage(message);
      setErrorMessage('');
    }
    
    setTimeout(() => {
      setSuccessMessage('');
      setErrorMessage('');
    }, 3000);
  };

  useEffect(() => {
    const fetchBoardData = async () => {
      try {
        const response = await fetch(`/request/getActionInfo?board_id=${tableName}`, { credentials: 'include' });
        const data = await response.json();
        if (data.action_info) {
          setValues(data.action_info);
        }
      } catch (error) {
        console.error('Error fetching board data:', error);
        showMessage('보드 데이터 로딩 실패', true);
      }
    };

    const fetchColumns = async () => {
      try {
        const response = await fetch(`/request/getColumnsInfo?boardId=${tableName}`, { credentials: 'include' });
        const data = await response.json();
        setColumns(data);
      } catch (error) {
        console.error('Error fetching columns:', error);
        showMessage('컬럼 데이터 로딩 실패', true);
      }
    };

    fetchBoardData();
    fetchColumns();
  }, [tableName, setValues]);

  useEffect(() => {
    const updatedOptions = columns.map(column => ({
      std_key: column.column_title,
      std_value: column.column_id
    }));
    setOptions(updatedOptions);
  }, [columns]);

  // 이벤트 핸들러들
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setMenuConfig(prevConfig => ({
      ...prevConfig,
      [name]: checked
    }));
  };

  const handleRadioChange = (event) => {
    const { name, value } = event.target;
    setMenuConfig(prevConfig => ({
      ...prevConfig,
      [name]: value === 'true'
    }));
  };

  const handleSelectChange = (event, field) => {
    const selectedValue = event.target.value;
    setMenuConfig(prevConfig => ({
      ...prevConfig,
      [field]: selectedValue
    }));
  };

  const handleApplyClick = async () => {
    try {
      const response = await fetch('/request/updateBoardInfo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          use_new: menuConfig.use_new,
          use_editer: menuConfig.use_editer,
          use_modify: menuConfig.use_modify,
          use_delete: menuConfig.use_delete,
          use_excel: menuConfig.use_excel,
          use_trigger: menuConfig.use_trigger,
          use_view: menuConfig.use_view,
          use_upload: menuConfig.use_upload,
          display_title: menuConfig.display_title,
          display_content: menuConfig.display_content,
          sort_order: menuConfig.sort_order,
          post_btn_type: menuConfig.post_btn_type,
          search_type: menuConfig.search_type,
          board_id: tableName
        }),
      });

      if (response.ok) {
        showMessage('설정이 성공적으로 저장되었습니다!');
      } else {
        showMessage('설정 저장 중 오류가 발생했습니다.', true);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      showMessage('서버에 연결할 수 없습니다.', true);
    }
  };

  const handleColumnSettingChange = (index, field, value) => {
    setColumns(prev =>
      prev.map((column, i) =>
        i === index ? { ...column, [field]: value } : column
      )
    );
  };

  const handleSaveColumnSettings = async () => {
    try {
      await fetch('/request/saveColumnSettings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board_id: tableName, columns })
      });
      showMessage('컬럼 설정이 성공적으로 저장되었습니다!');
    } catch (error) {
      console.error('Error saving column settings:', error);
      showMessage('컬럼 설정 저장 실패', true);
    }
  };

  const handleSaveControlSettings = async () => {
    try {
      await fetch('/request/saveControlSettings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board_id: tableName, columns })
      });
      showMessage('컨트롤 설정이 성공적으로 저장되었습니다!');
    } catch (error) {
      console.error('Error saving control settings:', error);
      showMessage('컨트롤 설정 저장 실패', true);
    }
  };

  // UI Control Settings 렌더링
  const renderControlSettings = () => (
    <div>
      {/* 기본 기능 설정 */}
      <FormCard>
        <FormTitle>
          <FontAwesomeIcon icon={faCog} />
          기본 기능 설정
        </FormTitle>
        <FormGrid>
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_new"
              checked={menuConfig.use_new}
              onChange={handleCheckboxChange}
            />
            <span>신규 등록</span>
          </MobileCheckbox>
          
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_modify"
              checked={menuConfig.use_modify}
              onChange={handleCheckboxChange}
            />
            <span>수정</span>
          </MobileCheckbox>
          
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_delete"
              checked={menuConfig.use_delete}
              onChange={handleCheckboxChange}
            />
            <span>삭제</span>
          </MobileCheckbox>
          
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_excel"
              checked={menuConfig.use_excel}
              onChange={handleCheckboxChange}
            />
            <span>엑셀</span>
          </MobileCheckbox>
          
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_upload"
              checked={menuConfig.use_upload}
              onChange={handleCheckboxChange}
            />
            <span>업로드</span>
          </MobileCheckbox>
          
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_view"
              checked={menuConfig.use_view}
              onChange={handleCheckboxChange}
            />
            <span>본문보기</span>
          </MobileCheckbox>
          
          <MobileCheckbox>
            <input
              type="checkbox"
              name="use_editer"
              checked={menuConfig.use_editer}
              onChange={handleCheckboxChange}
            />
            <span>에디터</span>
          </MobileCheckbox>
        </FormGrid>
      </FormCard>

      {/* 등록 버튼 타입 */}
      <FormCard>
        <FormTitle>
          <FontAwesomeIcon icon={faPlus} />
          등록 버튼 타입
        </FormTitle>
        <RadioGroup>
          <RadioOption selected={menuConfig.post_btn_type === true}>
            <input
              type="radio"
              name="post_btn_type"
              checked={menuConfig.post_btn_type === true}
              onChange={handleRadioChange}
              value="true"
            />
            <span>기본</span>
          </RadioOption>
          <RadioOption selected={menuConfig.post_btn_type === false}>
            <input
              type="radio"
              name="post_btn_type"
              checked={menuConfig.post_btn_type === false}
              onChange={handleRadioChange}
              value="false"
            />
            <span>확장</span>
          </RadioOption>
        </RadioGroup>
      </FormCard>

      {/* 검색 타입 */}
      <FormCard>
        <FormTitle>
          <FontAwesomeIcon icon={faDatabase} />
          검색 타입
        </FormTitle>
        <RadioGroup>
          <RadioOption selected={menuConfig.search_type === false}>
            <input
              type="radio"
              name="search_type"
              checked={menuConfig.search_type === false}
              onChange={handleRadioChange}
              value="false"
            />
            <span>기본 검색</span>
          </RadioOption>
          <RadioOption selected={menuConfig.search_type === true}>
            <input
              type="radio"
              name="search_type"
              checked={menuConfig.search_type === true}
              onChange={handleRadioChange}
              value="true"
            />
            <span>확장 검색</span>
          </RadioOption>
        </RadioGroup>
      </FormCard>

      {/* 컬럼 선택 */}
      <FormCard>
        <FormTitle>
          <FontAwesomeIcon icon={faColumns} />
          표시 컬럼 설정
        </FormTitle>
        <FormGrid>
          <FormField>
            <FieldLabel>타이틀 컬럼</FieldLabel>
            <SearchSelecter
              value={menuConfig.display_title}
              placeholder="타이틀 컬럼 선택"
              onChange={(e) => handleSelectChange(e, 'display_title')}
              options={options}
              SelectHeader={SelectHeader}
            />
          </FormField>
          
          <FormField>
            <FieldLabel>컨텐츠 컬럼</FieldLabel>
            <SearchSelecter
              value={menuConfig.display_content}
              placeholder="컨텐츠 컬럼 선택"
              onChange={(e) => handleSelectChange(e, 'display_content')}
              options={options}
              SelectHeader={SelectHeader}
            />
          </FormField>
        </FormGrid>
        
        {/* 데이터 정렬을 별도로 전체 너비로 배치 */}
        <FormField style={{ marginTop: '16px' }}>
          <FieldLabel>데이터 정렬</FieldLabel>
          <SearchSelecter
            value={menuConfig.sort_order}
            placeholder="정렬 방식 선택"
            onChange={(e) => handleSelectChange(e, 'sort_order')}
            options={[
              { std_key: '내림차순', std_value: 'desc' },
              { std_key: '오름차순', std_value: 'asc' }
            ]}
            SelectHeader={SelectHeader}
          />
        </FormField>
      </FormCard>

      <SaveButton variant="primary" onClick={handleApplyClick}>
        <FontAwesomeIcon icon={faSave} />
        설정 적용
      </SaveButton>
    </div>
  );

  // Display Column Settings 렌더링
  const renderColumnSettings = () => (
    <div>
      <TableHeader>컬럼 표시 설정</TableHeader>
      {columns.map((column, index) => (
        <ColumnItem key={index}>
          <ColumnHeader>
            <ColumnName>{column.column_title}</ColumnName>
            <div style={{ display: 'flex', gap: '8px' }}>
              <MobileCheckbox style={{ margin: 0, padding: '4px 8px' }}>
                <input
                  type="checkbox"
                  checked={column.cell_visible}
                  onChange={(e) => handleColumnSettingChange(index, 'cell_visible', e.target.checked)}
                />
                <span style={{ fontSize: '12px' }}>표시</span>
              </MobileCheckbox>
              <MobileCheckbox style={{ margin: 0, padding: '4px 8px' }}>
                <input
                  type="checkbox"
                  checked={column.read_only}
                  onChange={(e) => handleColumnSettingChange(index, 'read_only', e.target.checked)}
                />
                <span style={{ fontSize: '12px' }}>읽기전용</span>
              </MobileCheckbox>
            </div>
          </ColumnHeader>
          
          <ColumnFields>
            <FormField>
              <FieldLabel>매핑명</FieldLabel>
              <MobileInput
                value={column.mapping_name || ''}
                onChange={(e) => handleColumnSettingChange(index, 'mapping_name', e.target.value)}
                placeholder="매핑명"
              />
            </FormField>
            
            <FormField>
              <FieldLabel>너비</FieldLabel>
              <MobileInput
                value={column.cell_width_size || ''}
                onChange={(e) => handleColumnSettingChange(index, 'cell_width_size', e.target.value)}
                placeholder="너비"
              />
            </FormField>
            
            <FormField>
              <FieldLabel>함수</FieldLabel>
              <MobileInput
                value={column.function || ''}
                onChange={(e) => handleColumnSettingChange(index, 'function', e.target.value)}
                placeholder="함수"
              />
            </FormField>
            
            <FormField>
              <FieldLabel>결과 액션</FieldLabel>
              <MobileInput
                value={column.result_action || ''}
                onChange={(e) => handleColumnSettingChange(index, 'result_action', e.target.value)}
                placeholder="결과 액션"
              />
            </FormField>
            
            <FormField>
              <FieldLabel>마스크</FieldLabel>
              <MobileInput
                value={column.mask || ''}
                onChange={(e) => handleColumnSettingChange(index, 'mask', e.target.value)}
                placeholder="마스크"
              />
            </FormField>
            
            <FormField>
              <FieldLabel>컬럼 순서</FieldLabel>
              <MobileInput
                value={column.column_idx || ''}
                onChange={(e) => handleColumnSettingChange(index, 'column_idx', e.target.value)}
                placeholder="컬럼 순서"
              />
            </FormField>
          </ColumnFields>
        </ColumnItem>
      ))}
      
      <SaveButton onClick={handleSaveColumnSettings}>
        <FontAwesomeIcon icon={faSave} />
        컬럼 설정 저장
      </SaveButton>
    </div>
  );

  return (
    <MobileContainer>
      {/* 페이지 헤더 */}
      <PageHeader>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <PageTitle>보드 관리</PageTitle>
        <PageSubtitle>{tableName} 테이블 설정</PageSubtitle>
      </PageHeader>

      {/* 성공/에러 메시지 */}
      {successMessage && (
        <SuccessMessage>
          <FontAwesomeIcon icon={faCheck} />
          {successMessage}
        </SuccessMessage>
      )}
      
      {errorMessage && (
        <ErrorMessage>
          <FontAwesomeIcon icon={faTimes} />
          {errorMessage}
        </ErrorMessage>
      )}

      {/* UI Control Settings */}
      <SectionCard>
        <SectionHeader 
          isExpanded={expandedSections.control}
          color="#007bff"
          onClick={() => toggleSection('control')}
        >
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faCog} />
            </SectionIcon>
            <span>UI 기능 설정</span>
          </SectionTitle>
          <FontAwesomeIcon 
            icon={expandedSections.control ? faChevronUp : faChevronDown} 
          />
        </SectionHeader>
        <SectionContent isExpanded={expandedSections.control}>
          {renderControlSettings()}
        </SectionContent>
      </SectionCard>

      {/* Display Column Settings */}
      <SectionCard>
        <SectionHeader 
          isExpanded={expandedSections.column}
          color="#28a745"
          onClick={() => toggleSection('column')}
        >
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faColumns} />
            </SectionIcon>
            <span>컬럼 표시 설정</span>
          </SectionTitle>
          <FontAwesomeIcon 
            icon={expandedSections.column ? faChevronUp : faChevronDown} 
          />
        </SectionHeader>
        <SectionContent isExpanded={expandedSections.column}>
          {renderColumnSettings()}
        </SectionContent>
      </SectionCard>

      {/* Relation Table Settings */}
      <SectionCard>
        <SectionHeader 
          isExpanded={expandedSections.relationTable}
          color="#17a2b8"
          onClick={() => toggleSection('relationTable')}
        >
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faLink} />
            </SectionIcon>
            <span>관계 테이블 설정</span>
          </SectionTitle>
          <FontAwesomeIcon 
            icon={expandedSections.relationTable ? faChevronUp : faChevronDown} 
          />
        </SectionHeader>
        <SectionContent isExpanded={expandedSections.relationTable}>
          <RelationTableSettings 
            tableName={tableName}
            values={values}
            setValues={setValues}
          />
        </SectionContent>
      </SectionCard>

      {/* Column Management */}
      <SectionCard>
        <SectionHeader 
          isExpanded={expandedSections.columnManage}
          color="#dc3545"
          onClick={() => toggleSection('columnManage')}
        >
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faDatabase} />
            </SectionIcon>
            <span>마스터 테이블 컬럼 관리</span>
          </SectionTitle>
          <FontAwesomeIcon 
            icon={expandedSections.columnManage ? faChevronUp : faChevronDown} 
          />
        </SectionHeader>
        <SectionContent isExpanded={expandedSections.columnManage}>
          <ColumnManagement tableName={tableName} />
        </SectionContent>
      </SectionCard>

      {/* Control Management */}
      <SectionCard>
        <SectionHeader 
          isExpanded={expandedSections.controlManage}
          color="#6f42c1"
          onClick={() => toggleSection('controlManage')}
        >
          <SectionTitle>
            <SectionIcon>
              <FontAwesomeIcon icon={faGamepad} />
            </SectionIcon>
            <span>컨트롤 관리</span>
          </SectionTitle>
          <FontAwesomeIcon 
            icon={expandedSections.controlManage ? faChevronUp : faChevronDown} 
          />
        </SectionHeader>
        <SectionContent isExpanded={expandedSections.controlManage}>
          <ControlManagement tableName={tableName} />
        </SectionContent>
      </SectionCard>
    </MobileContainer>
  );
};

export default MobileManageBoard;